/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { CancelScheduledAssignmentResponse2 } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type CancelScheduledAssignmentParams = {
  scheduledJobId: string;
};

export type CancelScheduledAssignmentMutationConfig = {
  onSuccess?: (data: CancelScheduledAssignmentResponse2, variables: CancelScheduledAssignmentParams) => void;
  onError?: (error: Error, variables: CancelScheduledAssignmentParams) => void;
  onSettled?: (
    data: CancelScheduledAssignmentResponse2 | undefined,
    error: Error | null,
    variables: CancelScheduledAssignmentParams,
  ) => void;
  onMutate?: (variables: CancelScheduledAssignmentParams) => void;
};

/**
 * Hook for canceling a scheduled config assignment.
 *
 * API Endpoint:
 * - DELETE /tenants/:tenantId/scheduled-assignments/:scheduledJobId - Cancels a scheduled assignment
 *
 * @param config.onSuccess - Called after successful cancellation
 * @param config.onError - Called if cancellation fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 * @param config.onMutate - Called before mutation starts
 *
 * @returns React Query mutation result for canceling scheduled assignment
 *
 * @example
 * ```tsx
 * const { mutateAsync, isPending } = useCancelScheduledAssignmentMutation({
 *   onSuccess: () => console.log('Scheduled assignment cancelled'),
 *   onError: (error) => console.error('Failed to cancel:', error)
 * });
 *
 * await mutateAsync({ scheduledJobId: 12345 });
 * ```
 */
export function useCancelScheduledAssignmentMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: CancelScheduledAssignmentMutationConfig = {}) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, mutateAsync, isPending, isError, error } = useMutation<
    CancelScheduledAssignmentResponse2,
    Error,
    CancelScheduledAssignmentParams
  >({
    mutationFn: async ({ scheduledJobId }: CancelScheduledAssignmentParams) => {
      return mgmtPlaneAPIClient.nodeConfigs.cancelScheduledAssignment({
        tenantId: tenantId!,
        jobId: scheduledJobId,
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, mutateAsync, isPending, isError, error };
}
